import React, { useCallback, useMemo } from 'react';
import notify from 'devextreme/ui/notify';
import FileManager, {
  ItemView, Details, Column, Permissions,
} from 'devextreme-react/file-manager';
import CustomFileSystemProvider from 'devextreme/file_management/custom_provider';

import { useStaffListApiService } from '../../services';
import { appConst } from '../../app-const';
import './committee-file-manager.scss';

// Documents are stored without a content type, so the download response is always
// application/octet-stream. Infer a renderable MIME type from the file extension
// so the browser can display the file inline instead of downloading it.
const mimeTypeFromFileName = (fileName) => {
  const extension = fileName?.split('.').pop()?.toLowerCase();
  return appConst.mimeTypesByExtension[extension];
};

// Read-only context menu: members may only view/download documents (no upload/
// create/delete/rename). File-only commands are hidden for folders/empty space
// via onContextMenuShowing.
const contextMenuItems = (fileCommandsVisible) => [
  { text: 'View', icon: 'eyeopen', visible: fileCommandsVisible },
  { text: 'Download', icon: 'download', visible: fileCommandsVisible },
  'refresh',
];

// Maps a raw ListAttachments item (camelCase JSON from the API) to a DevExtreme
// file-system item. `name` is overridden with the leaf file name for display, so
// the full blob path is kept separately under `blobPath` for download/view.
const toFileSystemItem = (item) => (item.isDirectory
  ? { name: item.fileName, isDirectory: true }
  : {
    ...item,
    name: item.fileName,
    isDirectory: false,
    size: item.size,
    blobPath: item.name,
    // DevExtreme's "Date Modified" column expects a Date, not an ISO string.
    dateModified: item.dateUploaded ? new Date(item.dateUploaded) : undefined,
  });

// Read-only file explorer for a committee's documents in the member-access app.
// All HTTP lives in the StaffList API service; this component is the DevExtreme glue.
// Double-click opens an item (file -> preview, folder -> navigate, both native).
export default function CommitteeFileManager({ committeeId }) {
  const { listCommitteeDocumentsAsync, downloadCommitteeDocumentAsync } = useStaffListApiService();

  // Fetches the document and opens it in a new tab via a blob: URL
  // (PDFs/images render inline; other types download).
  const viewDocument = useCallback(async (blobPath) => {
    try {
      const rawBlob = await downloadCommitteeDocumentAsync(committeeId, blobPath);
      const contentType = mimeTypeFromFileName(blobPath) || rawBlob.type || 'application/octet-stream';
      const objectUrl = window.URL.createObjectURL(new Blob([rawBlob], { type: contentType }));
      window.open(objectUrl, '_blank', 'noopener noreferrer');
      window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60000);
    } catch (error) {
      notify({ message: error.message, shading: true }, 'error');
    }
  }, [downloadCommitteeDocumentAsync, committeeId]);

  // Saves the document to disk (rather than opening it inline).
  const downloadDocument = useCallback(async (blobPath, fileName) => {
    try {
      const rawBlob = await downloadCommitteeDocumentAsync(committeeId, blobPath);
      const objectUrl = window.URL.createObjectURL(rawBlob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60000);
    } catch (error) {
      notify({ message: error.message, shading: true }, 'error');
    }
  }, [downloadCommitteeDocumentAsync, committeeId]);

  const fileSystemProvider = useMemo(() => new CustomFileSystemProvider({
    getItems: async (pathInfo) => {
      const { data } = await listCommitteeDocumentsAsync(committeeId, pathInfo?.path ?? '');
      return (data ?? []).map(toFileSystemItem);
    },
  }), [committeeId, listCommitteeDocumentsAsync]);

  // Double-clicking (opening) a document previews it; folders navigate natively.
  const onSelectedFileOpened = useCallback(async (e) => {
    if (e.file && !e.file.isDirectory) {
      await viewDocument(e.file.dataItem.blobPath);
    }
  }, [viewDocument]);

  // The FileManager rebuilds its context menu each time it opens, so toggle the
  // file-only commands based on whether a file (not a folder) was right-clicked.
  const onContextMenuShowing = useCallback((e) => {
    const isFile = e.fileSystemItem && !e.fileSystemItem.isDirectory;
    e.component.option('contextMenu.items', contextMenuItems(!!isFile));
  }, []);

  // Row-level "View"/"Download" from the file context menu (right-click a document).
  const onContextMenuItemClick = useCallback(async (e) => {
    if (!e.fileSystemItem || e.fileSystemItem.isDirectory) return;
    if (e.itemData.text === 'View') {
      await viewDocument(e.fileSystemItem.dataItem.blobPath);
    } else if (e.itemData.text === 'Download') {
      await downloadDocument(e.fileSystemItem.dataItem.blobPath, e.fileSystemItem.name);
    }
  }, [viewDocument, downloadDocument]);

  return (
    <FileManager
      id="committeeDocumentsFileManager"
      height="75vh"
      fileSystemProvider={fileSystemProvider}
      contextMenu={{ items: contextMenuItems(true) }}
      onContextMenuShowing={onContextMenuShowing}
      onContextMenuItemClick={onContextMenuItemClick}
      onSelectedFileOpened={onSelectedFileOpened}
    >
      {/* Read-only: every mutating permission stays false (the default). */}
      <Permissions />
      <ItemView>
        <Details>
          <Column dataField="thumbnail" />
          <Column dataField="name" />
          <Column dataField="dateModified" />
          <Column dataField="size" />
          <Column dataField="uploadedBy" caption="Uploaded By" />
        </Details>
      </ItemView>
    </FileManager>
  );
}
